import piexif from 'piexifjs';
import { EXIF_FIELDS } from '../config/fieldDefinitions.js';
import {
  detectImageFormat,
  IMAGE_FORMATS,
  getMimeTypeForFormat,
  isMetadataWriteSupported,
} from './imageFormat.js';
import { normalizeDateTaken } from './dateTaken.js';
import {
  parseFiniteCoordinate,
  quantizeGpsPairToNearestMeter,
} from './gpsPrecision.js';

function normalizeFieldValue(fieldId, value) {
  if (fieldId === 'dateTaken') {
    return normalizeDateTaken(value);
  }
  return value === '' ? null : value;
}

// Helper functions
function arrayBufferToDataURL(buffer, mimeType = 'image/jpeg') {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return `data:${mimeType};base64,${base64}`;
}

function dataURLToArrayBuffer(dataUrl) {
  const base64 = dataUrl.split(',')[1];
  const binary = atob(base64);
  const buffer = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) {
    view[i] = binary.charCodeAt(i);
  }
  return buffer;
}

export async function readExifData(arrayBuffer, options = {}) {
  try {
    const format = detectImageFormat({
      fileName: options.fileName,
      mimeType: options.mimeType,
      buffer: new Uint8Array(arrayBuffer),
    });
    const mimeType = format === IMAGE_FORMATS.UNKNOWN
      ? 'image/jpeg'
      : options.mimeType || getMimeTypeForFormat(format);
    const dataUrl = arrayBufferToDataURL(arrayBuffer, mimeType);
    const exifObj = piexif.load(dataUrl);

    const result = {};

    // Dynamically read all fields from field definitions
    Object.values(EXIF_FIELDS).forEach(field => {
      try {
        const section = exifObj[field.exifPath];
        if (!section) {
          result[field.id] = normalizeFieldValue(field.id, field.defaultValue);
          return;
        }

        // Handle fields with reference tags (GPS coordinates, altitude)
        if (field.exifRefTag) {
          const value = section[field.exifTag];
          const ref = section[field.exifRefTag.tag];

          if (value !== undefined && value !== null) {
            result[field.id] = normalizeFieldValue(field.id, field.formatFromExif(value, ref));
          } else {
            result[field.id] = normalizeFieldValue(field.id, field.defaultValue);
          }
        } else {
          // Regular fields
          const value = section[field.exifTag];
          if (value !== undefined && value !== null) {
            result[field.id] = normalizeFieldValue(field.id, field.formatFromExif(value));
          } else {
            result[field.id] = normalizeFieldValue(field.id, field.defaultValue);
          }
        }
      } catch (error) {
        console.warn(`Failed to read field ${field.id}:`, error);
        result[field.id] = normalizeFieldValue(field.id, field.defaultValue);
      }
    });

    result.raw = exifObj;
    return result;
  } catch (error) {
    console.error('Failed to read EXIF:', error);

    // Return default values for all fields
    const defaultResult = { raw: null };
    Object.values(EXIF_FIELDS).forEach(field => {
      defaultResult[field.id] = normalizeFieldValue(field.id, field.defaultValue);
    });
    return defaultResult;
  }
}

export async function writeExifData(arrayBuffer, exifData, options = {}) {
  try {
    const format = detectImageFormat({
      fileName: options.fileName,
      mimeType: options.mimeType,
      buffer: new Uint8Array(arrayBuffer),
    });

    if (!isMetadataWriteSupported(format)) {
      throw new Error(`Metadata writing for ${format.toUpperCase()} is not supported yet`);
    }

    const dataUrl = arrayBufferToDataURL(arrayBuffer, 'image/jpeg');
    let exifObj = piexif.load(dataUrl);

    // Ensure objects exist
    if (!exifObj.Exif) exifObj.Exif = {};
    if (!exifObj.GPS) exifObj.GPS = {};
    if (!exifObj['0th']) exifObj['0th'] = {};

    let hasChanges = false;

    const normalizedExifData = { ...exifData };
    const latitude = parseFiniteCoordinate(exifData.latitude);
    const longitude = parseFiniteCoordinate(exifData.longitude);
    if (latitude !== null || longitude !== null) {
      const quantizedGps = quantizeGpsPairToNearestMeter(latitude, longitude);
      if (latitude !== null) {
        normalizedExifData.latitude = quantizedGps.latitude;
      }
      if (longitude !== null) {
        normalizedExifData.longitude = quantizedGps.longitude;
      }
    }

    // Dynamically write all fields
    Object.values(EXIF_FIELDS).forEach(field => {
      // Always stamp the Software tag with our product name
      const rawValue = field.id === 'software' ? 'Exiftrator' : normalizedExifData[field.id];
      const value = normalizeFieldValue(field.id, rawValue);

      // Skip if value is null, undefined, or default (empty string for text fields)
      if (value === null || value === undefined) {
        return;
      }
      if (value === field.defaultValue && (field.defaultValue === '' || field.defaultValue === null)) {
        return;
      }

      try {
        const section = exifObj[field.exifPath];

        // Handle fields with reference tags (GPS coordinates, altitude)
        if (field.exifRefTag) {
          const formatted = field.formatForExif(value);
          if (formatted) {
            if (formatted.dms) {
              // GPS coordinates
              section[field.exifTag] = formatted.dms;
              section[field.exifRefTag.tag] = formatted.ref;
            } else if (formatted.altitude !== undefined) {
              // Altitude
              section[field.exifTag] = formatted.altitude;
              section[field.exifRefTag.tag] = formatted.ref;
            }
            hasChanges = true;
          }
        } else {
          // Regular fields
          const formatted = field.formatForExif(value);
          if (formatted !== null && formatted !== undefined) {
            section[field.exifTag] = formatted;
            hasChanges = true;

            // Handle secondary tags (e.g., DateTimeOriginal also writes DateTimeDigitized)
            if (field.exifSecondaryTags) {
              field.exifSecondaryTags.forEach(secondaryTag => {
                exifObj[secondaryTag.path][secondaryTag.tag] = formatted;
              });
            }
          }
        }
      } catch (error) {
        console.warn(`Failed to write field ${field.id}:`, error);
      }
    });

    if (!hasChanges) {
      console.log('No changes to apply');
      return arrayBuffer;
    }

    // Convert back to binary
    const exifBytes = piexif.dump(exifObj);
    const newDataUrl = piexif.insert(exifBytes, dataUrl);

    return dataURLToArrayBuffer(newDataUrl);
  } catch (error) {
    if (!/not supported yet/i.test(error?.message || '')) {
      console.error('Failed to write EXIF:', error);
    }
    throw error;
  }
}
